/**
 * Helpers for GET /user/notifications API responses.
 */

export const extractNotificationsFromResponse = (response) => {
  const payload = response?.data;
  if (!payload) {
    return { items: [], error: null };
  }

  if (payload?.responseCode !== 200 && payload?.responseCode !== 201) {
    return {
      items: [],
      error: payload?.error?.message || payload?.message || null,
    };
  }

  const data = payload?.data;
  if (Array.isArray(data)) return { items: data, error: null };
  if (Array.isArray(data?.notifications)) return { items: data.notifications, error: null };
  if (Array.isArray(data?.list)) return { items: data.list, error: null };
  if (Array.isArray(data?.items)) return { items: data.items, error: null };

  return { items: [], error: null };
};

export const getNotificationStatus = (item = {}) =>
  String(item?.status ?? item?.readStatus ?? item?.state ?? '').trim().toUpperCase();

export const isNotificationRead = (item = {}) => {
  const status = getNotificationStatus(item);
  if (status) return status === 'READ';
  return Boolean(item?.isRead ?? item?.read ?? item?.is_read);
};

export const normalizeNotification = (item = {}, fallbackTitle = 'Notification') => ({
  id: item?.id || item?._id || item?.notificationId || item?.uuid,
  title:
    item?.title ||
    item?.subject ||
    item?.heading ||
    item?.notificationTitle ||
    fallbackTitle,
  message:
    item?.message ||
    item?.body ||
    item?.description ||
    item?.content ||
    item?.notificationMessage ||
    '',
  createdAt:
    item?.createdAt ||
    item?.timestamp ||
    item?.created_at ||
    item?.date ||
    item?.sentAt ||
    null,
  status: getNotificationStatus(item) || (isNotificationRead(item) ? 'READ' : ''),
  isRead: isNotificationRead(item),
  image: item?.image || item?.icon || item?.imageUrl || item?.thumbnail || null,
  type: item?.type || item?.notificationType || null,
});

export const countUnreadNotifications = (items = []) => {
  const unreadIds = new Set();
  let withoutId = 0;

  items.forEach((item) => {
    if (isNotificationRead(item)) return;
    const id = item?.id || item?._id || item?.notificationId || item?.uuid;
    if (id) unreadIds.add(String(id));
    else withoutId += 1;
  });

  return unreadIds.size + withoutId;
};

export const formatNotificationDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const isSameCalendarDay = (left, right) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

/** Figma 20246:17507 — time on card (e.g. 2:14 PM) */
export const formatNotificationTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const getNotificationGroupLabel = (value) => {
  if (!value) return 'Earlier';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Earlier';

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (isSameCalendarDay(date, today)) return 'Today';
  if (isSameCalendarDay(date, yesterday)) return 'Yesterday';

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const groupNotificationsByDate = (notifications = []) => {
  const groups = new Map();

  notifications.forEach((notification) => {
    const label = getNotificationGroupLabel(notification.createdAt);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(notification);
  });

  const order = ['Today', 'Yesterday'];
  return Array.from(groups.entries())
    .sort(([a], [b]) => {
      const aIdx = order.indexOf(a);
      const bIdx = order.indexOf(b);
      if (aIdx !== -1 || bIdx !== -1) {
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      }
      const aDate = new Date(a);
      const bDate = new Date(b);
      if (!Number.isNaN(aDate.getTime()) && !Number.isNaN(bDate.getTime())) {
        return bDate - aDate;
      }
      return a.localeCompare(b);
    })
    .map(([label, items]) => ({
      label,
      items: [...items].sort((a, b) => {
        const aTime = new Date(a.createdAt).getTime();
        const bTime = new Date(b.createdAt).getTime();
        if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
        return bTime - aTime;
      }),
    }));
};
